import React, { useState, useCallback } from 'react';
import { Eye, CheckCircle, XCircle, Clock, IndianRupee, TrendingUp, FileText, Image } from 'lucide-react';
import toast from 'react-hot-toast';
import FilterBar from '../components/FilterBar';
import Modal from '../components/Modal';
import { useFetch, useFilters } from '../hooks';
import { depositRequestService } from '../services/api';
import { formatCurrency, formatDate } from '../utils/helpers';

/* ── Filter config ─────────────────────────────────────────────────────────── */
const FILTER_INITIAL = { search: '', status: '', method: '', dateFrom: '', dateTo: '' };
const FILTER_FIELDS = [
  {
    key: 'status', label: 'Status', type: 'select',
    options: [
      { value: 'pending',  label: 'Pending'  },
      { value: 'approved', label: 'Approved' },
      { value: 'rejected', label: 'Rejected' },
    ],
  },
  {
    key: 'method', label: 'Payment Method', type: 'select',
    options: [
      { value: 'UPI',           label: 'UPI'           },
      { value: 'Bank Transfer', label: 'Bank Transfer' },
      { value: 'Credit Card',   label: 'Credit Card'   },
      { value: 'PayPal',        label: 'PayPal'        },
    ],
  },
  { key: 'dateFrom', label: 'From Date', type: 'date' },
  { key: 'dateTo',   label: 'To Date',   type: 'date' },
];
const filterFn = (r, f) => {
  if (f.search) {
    const q = f.search.toLowerCase();
    if (![r.id, r.patientName, r.reference, r.method].join(' ').toLowerCase().includes(q)) return false;
  }
  if (f.status && r.status   !== f.status) return false;
  if (f.method && r.method   !== f.method) return false;
  if (f.dateFrom && r.date   <  f.dateFrom) return false;
  if (f.dateTo   && r.date   >  f.dateTo)   return false;
  return true;
};

/* ── Status badge ──────────────────────────────────────────────────────────── */
const STATUS_MAP = {
  pending:  { cls: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300 ring-1 ring-yellow-200 dark:ring-yellow-800', icon: Clock        },
  approved: { cls: 'bg-green-100  text-green-800  dark:bg-green-900/40  dark:text-green-300  ring-1 ring-green-200  dark:ring-green-800',  icon: CheckCircle  },
  rejected: { cls: 'bg-red-100    text-red-800    dark:bg-red-900/40    dark:text-red-300    ring-1 ring-red-200    dark:ring-red-800',    icon: XCircle      },
};
const DepositStatusBadge = ({ status }) => {
  const { cls, icon: Icon } = STATUS_MAP[status] || STATUS_MAP.pending;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold capitalize whitespace-nowrap ${cls}`}>
      <Icon className="w-3 h-3" />{status}
    </span>
  );
};

/* ── Proof preview ─────────────────────────────────────────────────────────── */
const ProofPreview = ({ file, type }) => {
  if (!file) return <span className="text-xs text-gray-400 italic">No proof uploaded</span>;
  return (
    <div className="bg-gray-100 dark:bg-gray-700 rounded-xl p-8 flex flex-col items-center gap-3 min-h-[160px] justify-center">
      {type === 'pdf'
        ? <FileText className="w-14 h-14 text-red-400" />
        : <Image className="w-14 h-14 text-medihub-highlight" />
      }
      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{file}</p>
      <p className="text-xs text-gray-400">Preview not available in demo mode</p>
      <button
        onClick={() => toast('Download not available in demo mode')}
        className="text-xs text-medihub-primary dark:text-medihub-highlight underline hover:no-underline"
      >
        Download proof
      </button>
    </div>
  );
};

/* ── Detail Modal ──────────────────────────────────────────────────────────── */
const DetailModal = ({ request: r, onClose, onApprove, onReject }) => {
  if (!r) return null;
  return (
    <Modal isOpen={!!r} onClose={onClose} title={`Deposit Request — ${r.id}`} size="lg">
      <div className="space-y-4">
        {/* Patient info */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 space-y-2 text-sm">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Patient Info</p>
            {[
              { label: 'Name',  value: r.patientName  },
              { label: 'Email', value: r.patientEmail  },
              { label: 'Phone', value: r.patientPhone  },
            ].map(({ label, value }) => (
              <div key={label} className="flex justify-between gap-2">
                <span className="text-gray-500">{label}</span>
                <span className="font-medium text-gray-900 dark:text-white text-right">{value}</span>
              </div>
            ))}
          </div>
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 space-y-2 text-sm">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Payment Details</p>
            {[
              { label: 'Request ID', value: r.id },
              { label: 'Amount',     value: <span className="font-bold text-green-600 dark:text-green-400">{formatCurrency(r.amount)}</span> },
              { label: 'Method',     value: r.method },
              { label: 'Reference',  value: <span className="font-mono text-xs">{r.reference || '—'}</span> },
              { label: 'Date',       value: formatDate(r.date) },
              { label: 'Status',     value: <DepositStatusBadge status={r.status} /> },
            ].map(({ label, value }) => (
              <div key={label} className="flex justify-between items-center gap-2">
                <span className="text-gray-500">{label}</span>
                <span className="font-medium text-gray-900 dark:text-white text-right">{value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Notes */}
        {r.notes && (
          <div className="bg-medihub-light dark:bg-medihub-dark/20 rounded-xl p-3 text-sm">
            <p className="text-xs font-semibold text-medihub-primary dark:text-medihub-highlight mb-1">Patient Note</p>
            <p className="text-gray-700 dark:text-gray-300">{r.notes}</p>
          </div>
        )}
        {r.adminNote && (
          <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-3 text-sm">
            <p className="text-xs font-semibold text-purple-600 dark:text-purple-400 mb-1">Admin Note</p>
            <p className="text-gray-700 dark:text-gray-300">{r.adminNote}</p>
          </div>
        )}

        {/* Proof */}
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Payment Proof</p>
          <ProofPreview file={r.proofFile} type={r.proofType} />
        </div>

        {/* Actions */}
        {r.status === 'pending' && (
          <div className="flex justify-end gap-3 pt-1">
            <button onClick={onClose} className="btn-secondary">Close</button>
            <button
              onClick={() => { onReject(r); onClose(); }}
              className="flex items-center gap-1.5 text-sm font-medium px-4 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white transition-colors"
            >
              <XCircle className="w-4 h-4" />Reject
            </button>
            <button
              onClick={() => { onApprove(r); onClose(); }}
              className="flex items-center gap-1.5 text-sm font-medium px-4 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white transition-colors"
            >
              <CheckCircle className="w-4 h-4" />Approve
            </button>
          </div>
        )}
        {r.status !== 'pending' && (
          <div className="flex justify-between items-center pt-1">
            <p className="text-xs text-gray-500">Processed: {r.processedDate ? formatDate(r.processedDate) : '—'}</p>
            <button onClick={onClose} className="btn-secondary">Close</button>
          </div>
        )}
      </div>
    </Modal>
  );
};

/* ── Confirm Modal ─────────────────────────────────────────────────────────── */
const ConfirmModal = ({ data, onClose, onConfirm }) => {
  const [note, setNote] = useState('');
  if (!data) return null;
  const isApprove = data.action === 'approve';
  return (
    <Modal
      isOpen={!!data}
      onClose={onClose}
      title={isApprove ? 'Approve Deposit Request' : 'Reject Deposit Request'}
      size="sm"
    >
      <div className="space-y-4">
        <div className={`rounded-xl p-4 space-y-2 text-sm ${isApprove ? 'bg-green-50 dark:bg-green-900/20' : 'bg-red-50 dark:bg-red-900/20'}`}>
          {[
            { label: 'Request ID', value: data.request.id },
            { label: 'Patient',    value: data.request.patientName },
            { label: 'Amount',     value: formatCurrency(data.request.amount) },
            { label: 'Method',     value: data.request.method },
          ].map(({ label, value }) => (
            <div key={label} className="flex justify-between">
              <span className="text-gray-500">{label}</span>
              <span className="font-semibold text-gray-900 dark:text-white">{value}</span>
            </div>
          ))}
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {isApprove
            ? `This will credit ${formatCurrency(data.request.amount)} to the patient's wallet and mark the request as Approved.`
            : 'This will reject the request. No amount will be added to the patient wallet.'}
        </p>
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Admin Note (optional)</label>
          <input
            type="text"
            value={note}
            onChange={e => setNote(e.target.value)}
            className="input-field"
            placeholder="Add a note for this action…"
          />
        </div>
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button
            onClick={() => onConfirm(note)}
            className={`flex items-center gap-1.5 text-sm font-medium px-4 py-2 rounded-lg text-white transition-colors ${isApprove ? 'bg-green-600 hover:bg-green-700' : 'bg-red-500 hover:bg-red-600'}`}
          >
            {isApprove ? <><CheckCircle className="w-4 h-4" />Yes, Approve</> : <><XCircle className="w-4 h-4" />Yes, Reject</>}
          </button>
        </div>
      </div>
    </Modal>
  );
};

/* ── Page ──────────────────────────────────────────────────────────────────── */
const HISTORY_TABS = ['All', 'Pending', 'Approved', 'Rejected'];

const DepositRequests = () => {
  const { data: requests, loading, refetch } = useFetch(() => depositRequestService.getAll());
  const [activeTab, setActiveTab]   = useState('All');
  const [viewRequest, setViewRequest] = useState(null);
  const [confirm, setConfirm]       = useState(null); // { action, request }

  const filterFnCb = useCallback(filterFn, []);
  const { filters, onChange, onReset, filtered, activeCount } = useFilters(FILTER_INITIAL, filterFnCb, requests);

  /* Tab filter applied on top of search/field filters */
  const tabFiltered = activeTab === 'All'
    ? filtered
    : filtered.filter(r => r.status === activeTab.toLowerCase());

  /* Stats */
  const stats = {
    total:         requests?.length || 0,
    pending:       requests?.filter(r => r.status === 'pending').length  || 0,
    approved:      requests?.filter(r => r.status === 'approved').length || 0,
    rejected:      requests?.filter(r => r.status === 'rejected').length || 0,
    pendingAmount: requests?.filter(r => r.status === 'pending').reduce((s, r) => s + r.amount, 0) || 0,
  };

  const handleConfirm = async (note) => {
    if (!confirm) return;
    try {
      if (confirm.action === 'approve') {
        await depositRequestService.approve(confirm.request.id, note);
        toast.success(`Deposit of ${formatCurrency(confirm.request.amount)} approved`);
      } else {
        await depositRequestService.reject(confirm.request.id, note);
        toast.success('Deposit request rejected');
      }
      setConfirm(null);
      refetch();
    } catch (e) { toast.error(e.message || 'Action failed'); }
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Deposit Requests</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Manage patient wallet deposit requests</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          { label: 'Total',          value: stats.total,                        color: 'text-gray-900 dark:text-white',        bg: '' },
          { label: 'Pending',        value: stats.pending,                      color: 'text-yellow-700 dark:text-yellow-400', bg: 'bg-yellow-50 dark:bg-yellow-900/10' },
          { label: 'Approved',       value: stats.approved,                     color: 'text-green-700 dark:text-green-400',   bg: 'bg-green-50 dark:bg-green-900/10'   },
          { label: 'Rejected',       value: stats.rejected,                     color: 'text-red-600 dark:text-red-400',       bg: 'bg-red-50 dark:bg-red-900/10'       },
          { label: 'Pending Amount', value: formatCurrency(stats.pendingAmount), color: 'text-medihub-secondary dark:text-medihub-highlight',    bg: 'bg-medihub-light dark:bg-medihub-dark/10'     },
        ].map(({ label, value, color, bg }) => (
          <div key={label} className={`card p-4 text-center ${bg}`}>
            <p className={`text-xl font-bold ${color}`}>{value}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <FilterBar
        filters={filters}
        onChange={onChange}
        onReset={onReset}
        searchPlaceholder="Search by patient, ID, reference…"
        fields={FILTER_FIELDS}
        activeCount={activeCount}
      />

      {/* Table with tabs */}
      <div className="card overflow-hidden">
        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700 px-4 pt-4 flex items-center justify-between">
          <div className="flex gap-1 overflow-x-auto">
            {HISTORY_TABS.map(tab => {
              const count = tab === 'All' ? filtered.length : filtered.filter(r => r.status === tab.toLowerCase()).length;
              return (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${activeTab === tab ? 'border-medihub-primary text-medihub-primary dark:text-medihub-highlight' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`}
                >
                  {tab}
                  <span className="ml-1.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-1.5 py-0.5 rounded-full">{count}</span>
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-1.5 pr-2 shrink-0">
            <IndianRupee className="w-4 h-4 text-medihub-primary" />
            <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">Deposit Requests</span>
          </div>
        </div>

        {loading ? (
          <div className="p-6 space-y-3">
            {Array(4).fill(0).map((_, i) => <div key={i} className="h-12 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />)}
          </div>
        ) : tabFiltered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3 text-gray-400">
            <TrendingUp className="w-10 h-10 opacity-30" />
            <p className="text-sm font-medium">No deposit requests found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  {['Request ID', 'Patient', 'Amount', 'Method', 'Reference', 'Proof', 'Status', 'Date', 'Actions'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {tabFiltered.map(r => (
                  <tr key={r.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                    <td className="px-4 py-3 font-mono text-xs text-medihub-primary dark:text-medihub-highlight font-medium whitespace-nowrap">{r.id}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/40 rounded-full flex items-center justify-center text-purple-600 dark:text-purple-400 font-semibold text-xs shrink-0">
                          {r.patientName[0]}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">{r.patientName}</p>
                          <p className="text-xs text-gray-500">{r.patientEmail}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 font-bold text-gray-900 dark:text-white whitespace-nowrap">{formatCurrency(r.amount)}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">{r.method}</span>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">{r.reference || '—'}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {r.proofFile
                        ? <span className="inline-flex items-center gap-1 text-xs text-medihub-primary dark:text-medihub-highlight">
                            {r.proofType === 'pdf' ? <FileText className="w-3.5 h-3.5" /> : <Image className="w-3.5 h-3.5" />}
                            {r.proofType?.toUpperCase()}
                          </span>
                        : <span className="text-xs text-gray-400 italic">None</span>
                      }
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap"><DepositStatusBadge status={r.status} /></td>
                    <td className="px-4 py-3 text-gray-500 dark:text-gray-400 whitespace-nowrap">{formatDate(r.date)}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => setViewRequest(r)}
                          className="p-1.5 hover:bg-medihub-light dark:hover:bg-medihub-dark/20 rounded-lg transition-colors text-medihub-primary"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {r.status === 'pending' && (
                          <>
                            <button
                              onClick={() => setConfirm({ action: 'approve', request: r })}
                              className="flex items-center gap-1 px-2 py-1 text-xs font-medium bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-700 rounded-lg hover:bg-green-100 transition-colors"
                            >
                              <CheckCircle className="w-3.5 h-3.5" />Approve
                            </button>
                            <button
                              onClick={() => setConfirm({ action: 'reject', request: r })}
                              className="flex items-center gap-1 px-2 py-1 text-xs font-medium bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-700 rounded-lg hover:bg-red-100 transition-colors"
                            >
                              <XCircle className="w-3.5 h-3.5" />Reject
                            </button>
                          </>
                        )}
                        {r.status !== 'pending' && (
                          <span className="text-xs text-gray-400">{r.processedDate ? formatDate(r.processedDate) : '—'}</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      <DetailModal
        request={viewRequest}
        onClose={() => setViewRequest(null)}
        onApprove={r => setConfirm({ action: 'approve', request: r })}
        onReject={r  => setConfirm({ action: 'reject',  request: r })}
      />

      {/* Confirm Modal */}
      <ConfirmModal
        data={confirm}
        onClose={() => setConfirm(null)}
        onConfirm={handleConfirm}
      />
    </div>
  );
};

export default DepositRequests;
